import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle,
  MessageSquare,
  Phone,
  Users,
  Hash,
  Crown,
  Zap,
  ArrowRight,
  CheckCircle
} from "lucide-react";
import { useUpgradeModal } from "@/hooks/useUpgradeModal";

interface LimitReachedModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  limitType: 'sms_outbound' | 'voice_minutes' | 'team_members' | 'phone_numbers';
  currentUsage: number;
  limit: number;
  isTrial: boolean;
  message?: string;
}

const limitConfig = {
  sms_outbound: {
    icon: MessageSquare,
    title: 'SMS Limit Reached',
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-950/20',
    limitField: 'maxSmsPerMonth' as const
  },
  voice_minutes: {
    icon: Phone,
    title: 'Voice Minutes Limit Reached',
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-950/20',
    limitField: 'maxVoiceMinutesPerMonth' as const
  },
  team_members: {
    icon: Users,
    title: 'Team Member Limit Reached',
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-950/20',
    limitField: 'maxUsers' as const
  },
  phone_numbers: {
    icon: Hash,
    title: 'Phone Number Limit Reached',
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-50 dark:bg-orange-950/20',
    limitField: 'maxPhoneNumbers' as const
  }
};

export function LimitReachedModal({
  open,
  onOpenChange,
  limitType,
  currentUsage,
  limit,
  isTrial,
  message
}: LimitReachedModalProps) {
  const { openUpgradeModal } = useUpgradeModal();
  const config = limitConfig[limitType];
  const Icon = config.icon;
  const percentage = Math.min((currentUsage / limit) * 100, 100);

  // Fetch upgrade context to get next-tier plan details (database-driven)
  const { data: upgradeContext } = useQuery<any>({
    queryKey: ['/api/billing/upgrade-context'],
    staleTime: 60000,
    enabled: open // Only fetch when modal is open
  });

  // Find the recommended next-tier plan with higher limits
  const nextPlan = upgradeContext?.availablePlans?.find((plan: any) => {
    const planLimit = plan[config.limitField];
    // Find first plan with higher limit (or null/undefined for unlimited)
    return !planLimit || planLimit > limit;
  });

  // Build upgrade message with actual plan limits
  const getUpgradeMessage = () => {
    if (!nextPlan) return 'Upgrade to increase your limits';
    
    const limitValue = nextPlan[config.limitField];
    const limitDisplay = limitValue 
      ? limitValue.toLocaleString() 
      : 'Unlimited';
    
    const limitLabel = {
      maxSmsPerMonth: 'SMS/month',
      maxVoiceMinutesPerMonth: 'voice minutes/month',
      maxUsers: 'team members',
      maxPhoneNumbers: 'phone numbers'
    }[config.limitField];

    return `Upgrade to ${nextPlan.name} for ${limitDisplay} ${limitLabel}`;
  };

  // Get actual features from next plan
  const upgradeFeatures = nextPlan?.features?.slice(0, 4) || [
    'Increased limits',
    'Priority support',
    'Advanced features',
    'Better value'
  ];

  const handleUpgrade = () => {
    onOpenChange(false);
    openUpgradeModal(nextPlan?.id);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="limit-reached-modal">
        <DialogHeader>
          <div className={`mx-auto p-3 ${config.bgColor} rounded-full mb-4`}>
            <Icon className={`h-8 w-8 ${config.color}`} />
          </div>
          <DialogTitle className="text-center text-xl">
            {config.title}
          </DialogTitle>
          <DialogDescription className="text-center">
            {message || (isTrial 
              ? `You've reached your trial limit. Upgrade to continue using this feature.`
              : `You've reached your plan limit. Upgrade to unlock more.`
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Usage Progress */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">
                Current Usage
              </span>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="font-mono">
                  {currentUsage} / {limit}
                </Badge>
                <Badge variant="destructive">
                  {percentage.toFixed(0)}%
                </Badge>
              </div>
            </div>
            <Progress value={percentage} className="h-3" />
          </div>

          {/* Trial Badge */}
          {isTrial && (
            <div className="flex items-center gap-2 p-3 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg border border-yellow-200 dark:border-yellow-800/30">
              <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100">
                  Trial Account
                </p>
                <p className="text-xs text-yellow-700 dark:text-yellow-300">
                  Upgrade to unlock full features and remove limits
                </p>
              </div>
            </div>
          )}

          {/* Upgrade Benefits - Dynamic from database */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-gray-900 dark:text-gray-100">
              {getUpgradeMessage()}
            </h4>
            {nextPlan && (
              <div className="p-3 bg-surface-elevated dark:from-blue-950/20 dark:to-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800/30 mb-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {nextPlan.name}
                  </span>
                  <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                    ${parseFloat(nextPlan.basePrice).toFixed(0)}/{nextPlan.billingInterval}
                  </span>
                </div>
              </div>
            )}
            <div className="space-y-2">
              {upgradeFeatures.map((feature: string, index: number) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className={`h-4 w-4 ${config.color}`} />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {feature}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col gap-2 pt-2">
            <Button 
              onClick={handleUpgrade}
              className="w-full bg-blue-600 hover:from-blue-700 hover:to-blue-700 text-white"
              data-testid="upgrade-from-limit-modal"
            >
              <Crown className="h-4 w-4 mr-2" />
              Upgrade Now
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <Button 
              onClick={() => onOpenChange(false)}
              variant="outline"
              className="w-full"
              data-testid="close-limit-modal"
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Hook to manage limit modal state
export function useLimitReachedModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [limitData, setLimitData] = useState<{
    limitType: LimitReachedModalProps['limitType'];
    currentUsage: number;
    limit: number;
    isTrial: boolean;
    message?: string;
  } | null>(null);

  const showLimitModal = (data: {
    limitType: LimitReachedModalProps['limitType'];
    currentUsage: number;
    limit: number;
    isTrial: boolean;
    message?: string;
  }) => {
    setLimitData(data);
    setIsOpen(true);
  };

  const LimitModal = limitData ? (
    <LimitReachedModal
      open={isOpen}
      onOpenChange={setIsOpen}
      {...limitData}
    />
  ) : null;

  return {
    showLimitModal,
    LimitModal
  };
}
